/**
 * Utility functions for downloading files from the backend
 */

/**
 * Download a file from the backend API
 * @param {string} fileUrl - The relative or absolute URL to download from (e.g., '/api/download/file.csv')
 * @param {string} fileName - The name to save the file as
 */
export const downloadFile = (fileUrl, fileName) => {
  // Construct absolute URL for backend
  const backendUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const absoluteUrl = fileUrl.startsWith('http') ? fileUrl : `${backendUrl}${fileUrl}`;
  
  console.log(`📥 Downloading: ${fileName} from ${absoluteUrl}`);
  
  // Use fetch to download, then create blob
  fetch(absoluteUrl)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    })
    .then(blob => {
      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      console.log(`✅ Download completed: ${fileName}`);
    })
    .catch(error => {
      console.error(`❌ Download failed: ${error.message}`);
      alert(`Download failed: ${error.message}`);
    });
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
};

/**
 * Format date/time for display
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Check if it's an Excel serial date (number)
  if (typeof dateString === 'number') {
    // Excel dates are days since January 1, 1900
    const excelEpoch = new Date(1900, 0, 1);
    const daysOffset = dateString - 2; // Excel has a leap year bug for 1900
    const date = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
    return date.toLocaleDateString('en-US');
  }
  
  // Otherwise treat as regular date string
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US');
};